import React from 'react'

import { Icon, Section } from '@factorialco/deprecated-design-system'
import { type SelectOption } from '@factorialco/deprecated-design-system'
import moment from 'moment'

import { observer } from 'mobx-react'

import Country from 'modules/core/components/Inputs/Country'
import Select from 'modules/core/components/Inputs/Select'
import TextInput from 'modules/core/components/Inputs/Text'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import enableDay from 'modules/payroll_bundle/lib/enableDay'
import PolicyForm from 'modules/payroll_bundle/pages/settings/Components/PayrollPolicy/Form/PolicyForm'
import StartsOnDatePicker from 'modules/payroll_bundle/pages/settings/Components/PayrollPolicy/StartsOnDatePicker'
import Policy, { PeriodicityType } from 'modules/payroll_bundle/store/models/Policy'
import CurrencySelector from 'modules/settings/components/CurrencySelector'

interface Props {
  policy: Policy
}

const UpdatePolicySectionForm = ({ policy }: Props) => {
  const policyRecurrence = policy.get('recurrence')
  const policyForm = useLazyRef<PolicyForm>(
    () =>
      new PolicyForm({
        name: policy.get('name'),
        periodicity: Policy.periodicityKeyByRecurrence(policyRecurrence),
        starts_on: policy.get('starts_on'),
        recurrence: policyRecurrence,
        default: policy.get('default'),
        company_id: policy.get('company_id'),
        currency: policy.get('currency'),
        country: policy.get('country'),
      })
  ).current

  const { running, performAction } = useAsyncAction(() => policyForm.save(policy))
  const periodicities = Policy.periodicities()
  const periodicityOptions: SelectOption[] = Object.keys(periodicities).map((key) => ({
    label: periodicities[key as PeriodicityType].label ?? '',
    value: key,
  }))
  const enableDayForPolicy = (day: Date | moment.Moment): boolean => {
    const momentDay = moment(day)
    const recurrence =
      periodicities[policyForm.get('periodicity').value as PeriodicityType].recurrence
    return enableDay({ day: momentDay, recurrence })
  }

  const ffCopies = useMemo(() => {
    return {
      illustration: 'pencil' as React.ComponentProps<typeof Icon.Large>['icon'],
      title: i18n.t(
        'app_store_v2.apps.payroll_bundle.settings.payroll_policy.basic_info.title_self_onboarding'
      ),
      description: i18n.t(
        'app_store_v2.apps.payroll_bundle.settings.payroll_policy.basic_info.description_self_onboarding'
      ),
    }
  }, [])

  return (
    <Section.Form
      illustration={ffCopies.illustration}
      title={ffCopies.title}
      description={ffCopies.description}
      submitLabel={i18n.t('common.submit')}
      isDirty={policyForm.isDirty}
      onSubmit={performAction}
      disabled={running || !policyForm.get('name').value}
    >
      <TextInput
        field={policyForm.get('name')}
        label={i18n.t('payroll_bundle.payroll_policy.form.name')}
      />
      {policy.get('country') !== null && policy.get('currency') !== null && (
        <>
          <Country
            readonly
            label={i18n.t('payroll_bundle.payroll_policy.form.country')}
            field={policyForm.get('country')}
          />
          <CurrencySelector
            field={policyForm.get('currency')}
            label={i18n.t('payroll_bundle.payroll_policy.form.currency')}
            readonly
          />
        </>
      )}
      <Select
        field={policyForm.get('periodicity')}
        label={i18n.t('payroll_bundle.payroll_policy.form.periodicity')}
        options={periodicityOptions}
        readonly
      />
      <StartsOnDatePicker
        form={policyForm}
        label={i18n.t('payroll_bundle.payroll_policy.form.starts_on_self_onboarding')}
        readonly
        enableDay={enableDayForPolicy}
        periodicity={policyForm.get('periodicity').value}
      />
    </Section.Form>
  )
}

export default observer(UpdatePolicySectionForm)
